/** Canonical invoice status label — must match backend `status=Revised`. */
export const REVISED_STATUS = 'Revised'

function statusKey(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

/** True when value is the Revised workflow status (any casing). */
export function isRevisedStatusValue(value) {
  return statusKey(value) === 'revised'
}

/**
 * Pull live invoice.status out of a PUT /invoice/update response.
 * Ignores envelope status ("OK" / "ERROR"). Returns null when unknown.
 */
export function extractUpdatedInvoiceStatus(res) {
  if (!res || typeof res !== 'object') return null
  const rejectEnvelope = (s) => {
    const k = statusKey(s)
    return !k || k === 'ok' || k === 'error' || k === 'validation_error' || k === 'service_unavailable'
  }
  const fromObj = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null
    if (obj.status == null || obj.status === '') return null
    const s = String(obj.status).trim()
    return rejectEnvelope(s) ? null : s
  }

  const payload = res.data
  // { status: 'OK', data: { invoice: { status: 'Revised' } } }
  if (payload && typeof payload === 'object') {
    const nestedInvoice = payload.invoice
      || (payload.data && payload.data.invoice)
      || null
    const fromInvoice = fromObj(nestedInvoice)
    if (fromInvoice) return fromInvoice
    // Flat invoice row on data (has invoice_id / id + workflow status)
    if (payload.invoice_id || payload.id || payload.invoice_number) {
      const flat = fromObj(payload)
      if (flat) return flat
    }
    if (payload.data && typeof payload.data === 'object'
      && (payload.data.invoice_id || payload.data.id || payload.data.invoice_number)) {
      const flatNested = fromObj(payload.data)
      if (flatNested) return flatNested
    }
  }
  return null
}

/** Default workflow statuses when API does not return statusList. */
export const DEFAULT_INVOICE_STATUS_OPTIONS = [
  { text: 'Draft', value: 'Draft' },
  { text: 'Pending Approval', value: 'Pending Approval' },
  { text: 'Approved', value: 'Approved' },
  { text: 'Issued', value: 'Issued' },
  { text: 'Raised', value: 'Raised' },
  { text: REVISED_STATUS, value: REVISED_STATUS },
  { text: 'Part Paid', value: 'Part Paid' },
  { text: 'Paid', value: 'Paid' },
  { text: 'Unpaid', value: 'Unpaid' },
  { text: 'Overdue', value: 'Overdue' },
  { text: 'Disputed', value: 'Disputed' },
  { text: 'Cancelled', value: 'Cancelled' },
  { text: 'Early Attrition', value: 'Early Attrition' }
]

/**
 * Normalize a status option from API or local config to { text, value }.
 */
export function normalizeInvoiceStatusOption(opt) {
  if (typeof opt === 'string') {
    const str = opt.trim()
    return str ? { text: str, value: str } : null
  }
  if (!opt || typeof opt !== 'object') return null
  const raw = opt.title || opt.text || opt.label || opt.name || opt.value || opt.status || ''
  const str = String(raw || '').trim()
  if (!str) return null
  const val = opt.value != null ? String(opt.value).trim() : str
  return { text: str, value: val || str }
}

/**
 * Merge API statusList with canonical options, ensuring Revised is always present
 * (after Raised, or before Cancelled when Raised is absent).
 */
export function mergeInvoiceStatusOptions(apiList) {
  const source = Array.isArray(apiList) && apiList.length
    ? apiList
    : DEFAULT_INVOICE_STATUS_OPTIONS

  const mapped = source
    .map(normalizeInvoiceStatusOption)
    .filter(Boolean)

  if (mapped.some(o => statusKey(o.value) === 'revised')) {
    return mapped
  }

  const revisedOption = { text: REVISED_STATUS, value: REVISED_STATUS }
  const raisedIdx = mapped.findIndex(o => statusKey(o.value) === 'raised')
  if (raisedIdx >= 0) {
    mapped.splice(raisedIdx + 1, 0, revisedOption)
    return mapped
  }

  const cancelledIdx = mapped.findIndex(o => statusKey(o.value) === 'cancelled')
  if (cancelledIdx >= 0) {
    mapped.splice(cancelledIdx, 0, revisedOption)
    return mapped
  }

  mapped.push(revisedOption)
  return mapped
}

/** Options shaped for v-select in UpdateInvoice ({ title, label }). */
export function toVSelectStatusOptions(apiList) {
  return mergeInvoiceStatusOptions(apiList).map(o => ({
    title: o.text,
    label: o.text
  }))
}
